import axios, {
  type AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  type AxiosResponse,
  type CreateAxiosDefaults,
  InternalAxiosRequestConfig,
} from 'axios';
import memoize from 'memoize';
import toast from 'react-hot-toast';

import { scheduleTokenRefresh } from '../schedule-token-refresh';

interface RefreshTokenRequestProps {
  options: CreateAxiosDefaults;
  domainUrl: string;
}

interface ResponseErrorProps extends RefreshTokenRequestProps {
  axios: AxiosInstance;
}

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  retryCount?: number;
  showToast?: boolean;
}

interface ExtendedInternalAxiosRequestConfig extends InternalAxiosRequestConfig {
  retryCount?: number;
  showToast?: boolean;
}

const createAbortController = () => new AbortController();

export const sleep = (delay = 2000): Promise<void> => new Promise((resolve) => setTimeout(resolve, delay));

// The refresh token itself is never read client-side — it lives only in the
// httpOnly `hp-auth` cookie, sent automatically via withCredentials. The
// backend's RefreshTokenGuard reads it from that cookie (see
// refresh-token.strategy.ts), not from the request body.
const refreshTokenRequest = async ({ options, domainUrl }: RefreshTokenRequestProps) => {
  const abortController = createAbortController();
  const axiosClient = axios.create({ ...options, timeout: 8000 });

  try {
    const { data: tokens } = await axiosClient.post(
      '/user/auth/refresh-token',
      {},
      {
        withCredentials: true,
        headers: { domain: domainUrl || window.location.hostname },
        signal: abortController.signal,
      },
    );

    return tokens.data;
  } catch (error) {
    abortController.abort();

    if ((error as AxiosError).response?.status === 401) {
      window.location.href = '/login';
    }
    return null;
  }
};

// Memoized version of refreshTokenRequest to prevent duplicate calls within 10 seconds
const memoRefreshTokenRequest = memoize(refreshTokenRequest, { maxAge: 10000 });

const showErrorToast = (error: AxiosError<ServerResponse>) => {
  const showToast = error?.config?.headers?.['show-toast'];
  if (showToast === 'false') return;

  const status = error.response?.status ?? 0;
  const message = error.response?.data?.message || error.response?.data?.detail || 'Something went wrong!';

  if (status >= 400 && status < 500) {
    toast.error(message, {
      style: {
        background: '#facc15',
        color: '#fff',
        marginTop: 'env(safe-area-inset-top)',
      },
    });
  } else if (status >= 500) {
    toast.error(message, {
      style: { marginTop: 'env(safe-area-inset-top)' },
    });
  }
};

// No Authorization header — the httpOnly `hp-auth` cookie carries the access
// token automatically on every request (withCredentials: true on the client).
export const requestSuccess =
  ({ domainUrl }: { domainUrl?: string }) =>
  async (request: ExtendedInternalAxiosRequestConfig) => {
    request.headers = new AxiosHeaders({
      ...request.headers,
      domain: domainUrl || window.location.hostname,
      'show-toast': request?.showToast !== undefined ? `${request.showToast}` : 'false',
    });

    return request;
  };

export const requestError = async (error: AxiosError) => await Promise.reject(error);

export const responseSuccess = (response: AxiosResponse<ServerResponse>) => response.data.data! || response.data;

export const responseError =
  ({ options, domainUrl, axios }: ResponseErrorProps) =>
  async (error: AxiosError<ServerResponse>) => {
    const request = error.config as ExtendedAxiosRequestConfig;
    if (!request) return Promise.reject(error);

    if (error.response?.data?.message === 'JWT_EXPIRED') {
      if (request.retryCount && request.retryCount >= 1) {
        return Promise.reject(error);
      }

      request.retryCount = (request.retryCount ?? 0) + 1;
      const newTokens = await memoRefreshTokenRequest({ options, domainUrl });

      if (newTokens?.expiration) {
        scheduleTokenRefresh(newTokens.expiration, async () => await memoRefreshTokenRequest({ options, domainUrl }));
        // The refreshed access token is already in the cookie (Set-Cookie on
        // the refresh response) — just retry, withCredentials sends it.
        return await axios(request);
      }

      return Promise.reject(error);
    }

    if (error.response?.data?.message === 'JWT_MALFORMED') {
      return Promise.reject(error);
    }

    showErrorToast(error);

    return await Promise.reject(error.response?.data || error);
  };
